import { DatePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { SupportCallsService } from './support-calls.service';

@Component({
  selector: 'elite-support-calls',
  imports: [DatePipe],
  template: `
    @if (calls.visible()) {
      <aside class="call-console" aria-label="Support calls">
        <button class="heading" (click)="expanded.set(!expanded())" [attr.aria-expanded]="expanded()">
          <span aria-hidden="true">☎</span> Support calls
          <span>{{ calls.active() ? calls.status() : calls.available() ? 'Available' : 'Off duty' }}</span>
        </button>
        @if (expanded() || calls.incoming().length || calls.active()) {
          <div class="body">
            <label class="availability"><input type="checkbox" [checked]="calls.available()"
              [disabled]="calls.changingAvailability() || calls.starting() || !!calls.active()"
              (change)="calls.setAvailable($any($event.target).checked)" /> Available for calls</label>
            <small>{{ calls.connection() }}</small>
            @if (calls.error()) { <p class="error" role="alert">{{ calls.error() }}</p> }
            @for (call of calls.incoming(); track call.callId) {
              <section class="ring" role="alert">
                <strong>{{ call.callerName || 'Incoming support call' }}</strong>
                <p>Incoming voice call</p>
                <div class="actions">
                  <button class="answer" (click)="calls.accept(call)">Answer</button>
                  <button (click)="calls.dismiss(call.callId)">Dismiss here</button>
                </div>
              </section>
            }
            @if (calls.active(); as call) {
              <section class="ring" aria-live="polite">
                <strong>{{ call.callerName || 'Support caller' }}</strong>
                <p>{{ calls.status() }} · {{ duration(calls.seconds()) }}</p>
                <div class="actions">
                  <button [attr.aria-pressed]="calls.muted()" (click)="calls.setMuted()">{{ calls.muted() ? 'Unmute' : 'Mute' }}</button>
                  <button class="hangup" (click)="calls.end()">End call</button>
                </div>
                @if (calls.audioBlocked()) { <button (click)="calls.enableAudio()">Enable audio</button> }
              </section>
            }
            <details (toggle)="$any($event.target).open && calls.refreshHistory()">
              <summary>Recent support calls</summary>
              @for (call of calls.history(); track call.callId) {
                <p class="history">{{ call.startedAt | date:'short' }} · {{ call.status }} · {{ duration(call.durationSeconds) }}</p>
              } @empty { <p>No recent calls.</p> }
            </details>
          </div>
        }
      </aside>
    }
  `,
  styles: `
    :host { position: fixed; inset-block-end: 1rem; inset-inline-end: 1rem; z-index: 1100; }
    .call-console { width: min(360px, calc(100vw - 2rem)); border: 1px solid var(--border); border-radius: 14px;
      background: var(--card); color: var(--card-foreground); box-shadow: 0 8px 32px #0002; }
    button { border: 1px solid var(--border); border-radius: 8px; padding: .6rem .85rem; cursor: pointer; }
    button:disabled { cursor: default; opacity: .6; }
    button:focus-visible, input:focus-visible, summary:focus-visible { outline: 3px solid var(--ring); outline-offset: 2px; }
    .heading { display: flex; gap: .65rem; align-items: center; width: 100%; border: 0; font-weight: 600; text-align: start; }
    .heading span:last-child { margin-inline-start: auto; font-size: .75rem; font-weight: normal; }
    .body { padding: 1rem; max-height: 70vh; overflow: auto; }
    .availability, .actions { display: flex; align-items: center; gap: .75rem; }
    .availability input { width: 1.1rem; height: 1.1rem; }
    small { color: var(--muted-foreground); }
    .ring { padding: 1rem 0; border-block: 1px solid var(--border); margin: 1rem 0; }
    p { margin: .5rem 0; } .answer { background: var(--success); color: var(--success-foreground); }
    .hangup { background: var(--destructive); color: var(--destructive-foreground); }
    .error { color: var(--destructive); font-size: .875rem; } details { margin-top: 1rem; } summary { cursor: pointer; }
    .history { font-size: .8rem; padding-block: .4rem; border-bottom: 1px solid var(--border); }
  `,
})
export class SupportCallsComponent {
  readonly calls = inject(SupportCallsService);
  readonly expanded = signal(false);
  duration(seconds: number): string { return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`; }
}
